/**
 * Bond-tent allocation glidepath — Phase 6 Session 3 (the FIRE
 * sophistication layer).
 *
 * Wade Pfau ("Reducing Retirement Risk with a Rising Equity Glidepath",
 * Journal of Financial Planning 2014) and Michael Kitces showed that
 * sequence-of-returns risk peaks in the years immediately around
 * retirement: a 30% drawdown in year 1 of retirement is far more
 * destructive than a 30% drawdown in year 25 (the portfolio has fewer
 * future contributions to offset the loss + needs the depleted base
 * for far longer).
 *
 * The mitigation: glide UP in bond allocation as you approach
 * retirement (defusing equity exposure during the most vulnerable
 * years), then optionally REVERSE-glide back into equities once safely
 * past the danger zone. v1 ships **monotone rise-and-hold** — bonds
 * climb from `floor_pct` to `ceiling_pct` between (retirement_age -
 * ramp_start_years_before) and (retirement_age + ramp_end_years_after),
 * then hold at the ceiling for the rest of the projection. Pfau's
 * full reverse-glide variant is deferred to Phase 11 polish.
 *
 * **Glidepath function shape**: caller passes `bondTentAllocation` to
 * Trinity / Monte Carlo, which calls it once per simulation year with
 * the current age. Pure — no side effects.
 *
 * Mirrored verbatim at web/src/lib/util/bond-tent.ts per the 5.1 lock.
 */

export interface BondTentConfig {
  readonly enabled: boolean;
  readonly retirement_age: number;
  /** Years before retirement_age the ramp starts. Default 5 (Pfau/Kitces canonical). */
  readonly ramp_start_years_before?: number;
  /** Years after retirement_age the ramp ends. Default 5. */
  readonly ramp_end_years_after?: number;
  /** Bond % at start of ramp. Default 40 (60/40 baseline). */
  readonly bond_floor_pct?: number;
  /** Bond % at peak (end of ramp + held thereafter). Default 60. */
  readonly bond_ceiling_pct?: number;
}

export interface AllocationSnapshot {
  readonly equity_pct: number;
  readonly bond_pct: number;
}

export const BOND_TENT_DEFAULTS = {
  ramp_start_years_before: 5,
  ramp_end_years_after: 5,
  bond_floor_pct: 40,
  bond_ceiling_pct: 60,
} as const;

/**
 * Compute equity / bond allocation at a given age under the bond-tent
 * glidepath.
 *
 *   age < retirement_age - ramp_start_years_before     → bond_floor_pct
 *   age in [start, end]                                → linear interp
 *   age > retirement_age + ramp_end_years_after        → bond_ceiling_pct
 *
 * Returns `{ equity_pct, bond_pct }` with bond_pct in [floor, ceiling]
 * and equity_pct = 100 - bond_pct.
 */
export function bondTentAllocation(input: {
  readonly age: number;
  readonly config: BondTentConfig;
}): AllocationSnapshot {
  const { age, config } = input;
  const startYearsBefore =
    config.ramp_start_years_before ??
    BOND_TENT_DEFAULTS.ramp_start_years_before;
  const endYearsAfter =
    config.ramp_end_years_after ?? BOND_TENT_DEFAULTS.ramp_end_years_after;
  const floorPct = config.bond_floor_pct ?? BOND_TENT_DEFAULTS.bond_floor_pct;
  const ceilingPct =
    config.bond_ceiling_pct ?? BOND_TENT_DEFAULTS.bond_ceiling_pct;

  const rampStartAge = config.retirement_age - startYearsBefore;
  const rampEndAge = config.retirement_age + endYearsAfter;
  const rampSpan = rampEndAge - rampStartAge;

  let bondPct: number;
  if (age <= rampStartAge || rampSpan <= 0) {
    bondPct = floorPct;
  } else if (age >= rampEndAge) {
    bondPct = ceilingPct;
  } else {
    const fraction = (age - rampStartAge) / rampSpan;
    bondPct = floorPct + (ceilingPct - floorPct) * fraction;
  }

  // Clamp to [0, 100] in case caller mis-set floor/ceiling outside that
  // range; equity is the complement.
  const bond = Math.max(0, Math.min(100, bondPct));
  return { equity_pct: 100 - bond, bond_pct: bond };
}
